/**
 * Phone number formatting utilities
 *
 * Handles conversion between:
 * - Structured format: +(CC)NNNNNNNN (where CC is country calling code in parentheses)
 * - E.164 format: +CCNNNNNNNN (standard international format)
 *
 * The structured format ensures unambiguous parsing of country codes.
 */
import { parsePhoneNumber } from 'libphonenumber-js';
/**
 * Format phone number as +(CC)NNNNNNNN
 * @param countryCode - Country calling code (e.g., "90", "49", "1")
 * @param nationalNumber - National number without country code
 * @returns Formatted phone number like "+(90)2122009988"
 */
export function formatPhoneNumber(countryCode, nationalNumber) {
    if (!countryCode || !nationalNumber) {
        return '';
    }
    // Remove any non-digit characters from country code and national number
    const cleanCountryCode = countryCode.replace(/\D/g, '');
    const cleanNationalNumber = nationalNumber.replace(/\D/g, '');
    if (!cleanCountryCode || !cleanNationalNumber) {
        return '';
    }
    return `+(${cleanCountryCode})${cleanNationalNumber}`;
}
/**
 * Parse phone number from +(CC)NNNNNNNN format
 * @param formatted - Phone number in structured format like "+(90)2122009988"
 * @returns Object with countryCode and nationalNumber, or null if parsing fails
 */
export function parsePhoneNumberStructured(formatted) {
    if (!formatted || typeof formatted !== 'string') {
        return null;
    }
    // Match pattern: +(CC)NNNNNNNN
    const match = formatted.match(/^\+\((\d{1,3})\)(.+)$/);
    if (match) {
        return {
            countryCode: match[1],
            nationalNumber: match[2],
        };
    }
    return null;
}
/**
 * Convert E.164 format (+CCNNNNNNNN) to structured format (+(CC)NNNNNNNN)
 * Uses libphonenumber-js to parse the E.164 number and extract country code
 * @param e164 - Phone number in E.164 format like "+902122009988"
 * @returns Phone number in structured format like "+(90)2122009988", or original if parsing fails
 */
export function convertFromE164(e164) {
    if (!e164 || typeof e164 !== 'string') {
        return e164 || '';
    }
    // If already in structured format, return as-is
    if (e164.match(/^\+\(\d{1,3}\)/)) {
        return e164;
    }
    // If not starting with +, try to parse as-is or return original
    if (!e164.startsWith('+')) {
        // Try to parse with libphonenumber-js
        try {
            const parsed = parsePhoneNumber(e164);
            if (parsed) {
                const countryCallingCode = parsed.countryCallingCode;
                const nationalNumber = parsed.nationalNumber;
                return formatPhoneNumber(countryCallingCode, nationalNumber);
            }
        }
        catch {
            // If parsing fails, return original
            return e164;
        }
        return e164;
    }
    // Parse E.164 format using libphonenumber-js
    try {
        const parsed = parsePhoneNumber(e164);
        if (parsed) {
            const countryCallingCode = parsed.countryCallingCode;
            const nationalNumber = parsed.nationalNumber;
            return formatPhoneNumber(countryCallingCode, nationalNumber);
        }
    }
    catch (error) {
        // If parsing fails, return original format
        // This handles edge cases where libphonenumber-js can't parse the number
        return e164;
    }
    return e164;
}
/**
 * Convert structured format (+(CC)NNNNNNNN) to E.164 format (+CCNNNNNNNN)
 * @param formatted - Phone number in structured format like "+(90)2122009988"
 * @returns Phone number in E.164 format like "+902122009988", or original if parsing fails
 */
export function convertToE164(formatted) {
    if (!formatted || typeof formatted !== 'string') {
        return formatted || '';
    }
    // If already in E.164 format (starts with + but no parentheses), return as-is
    if (formatted.startsWith('+') && !formatted.includes('(')) {
        return formatted;
    }
    // Parse structured format
    const parsed = parsePhoneNumberStructured(formatted);
    if (parsed) {
        return `+${parsed.countryCode}${parsed.nationalNumber}`;
    }
    // If not in structured format, try to parse with libphonenumber-js and convert to E.164
    try {
        const parsed = parsePhoneNumber(formatted);
        if (parsed && parsed.isValid()) {
            return parsed.number; // Returns E.164 format
        }
    }
    catch {
        // If parsing fails, return original
        return formatted;
    }
    return formatted;
}
/**
 * Normalize phone number for storage
 * Accepts both E.164 and structured format, converts to structured format
 * @param phone - Phone number in any format
 * @returns Phone number in structured format +(CC)NNNNNNNN
 */
export function normalizePhoneForStorage(phone) {
    if (!phone || phone.trim() === '') {
        return '';
    }
    // If already in structured format, return as-is
    if (phone.match(/^\+\(\d{1,3}\)/)) {
        return phone;
    }
    // Convert from E.164 or other formats
    return convertFromE164(phone);
}
/**
 * Format phone number for display
 * Converts structured format to a more readable format with spaces
 * @param phone - Phone number in structured format
 * @returns Formatted phone number for display (e.g., "+(90) 212 200 99 88")
 */
export function formatPhoneForDisplay(phone) {
    if (!phone || phone.trim() === '') {
        return '';
    }
    // Parse structured format
    const parsed = parsePhoneNumberStructured(phone);
    if (parsed) {
        // Add spaces for readability (every 3 digits)
        const nationalNumber = parsed.nationalNumber.replace(/(\d{3})(?=\d)/g, '$1 ');
        return `+(${parsed.countryCode}) ${nationalNumber}`;
    }
    // If not in structured format, try to format E.164
    try {
        const parsed = parsePhoneNumber(phone);
        if (parsed) {
            // Format as international format with spaces
            return parsed.formatInternational();
        }
    }
    catch {
        // If parsing fails, return original
        return phone;
    }
    return phone;
}
/**
 * Format phone number for external APIs (Twilio, WhatsApp, etc.)
 * Converts structured format to E.164 format
 * @param phone - Phone number in structured format
 * @returns Phone number in E.164 format
 */
export function formatPhoneForAPI(phone) {
    if (!phone || phone.trim() === '') {
        return '';
    }
    return convertToE164(phone);
}
/**
 * Check if phone number is in structured format
 * @param phone - Phone number to check
 * @returns true if phone is in +(CC)NNNNNNNN format
 */
export function isStructuredFormat(phone) {
    if (!phone || typeof phone !== 'string') {
        return false;
    }
    return /^\+\(\d{1,3}\)/.test(phone);
}
/**
 * Check if phone number is in E.164 format
 * @param phone - Phone number to check
 * @returns true if phone is in +CCNNNNNNNN format
 */
export function isE164Format(phone) {
    if (!phone || typeof phone !== 'string') {
        return false;
    }
    return phone.startsWith('+') && !phone.includes('(') && /^\+[1-9]\d{1,14}$/.test(phone);
}
